import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { AnalyticsDateRange } from '../../types/api/dashboard';

// ────────────────────────── Presets ──────────────────────────────

export type DateRangePresetId =
  | 'today'
  | 'last7'
  | 'last30'
  | 'last90'
  | 'mtd'
  | 'custom';

interface PresetDef {
  id: DateRangePresetId;
  label: string;
  /** Returns the range, or null for the "custom" placeholder. */
  resolve: (now: Date) => AnalyticsDateRange | null;
}

function toIsoDate(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function shiftDays(d: Date, days: number): Date {
  const next = new Date(d);
  next.setDate(d.getDate() + days);
  return next;
}

const PRESETS: PresetDef[] = [
  {
    id: 'today',
    label: 'Today',
    resolve: (now) => ({ from: toIsoDate(now), to: toIsoDate(now) }),
  },
  {
    id: 'last7',
    label: 'Last 7 days',
    resolve: (now) => ({ from: toIsoDate(shiftDays(now, -6)), to: toIsoDate(now) }),
  },
  {
    id: 'last30',
    label: 'Last 30 days',
    resolve: (now) => ({ from: toIsoDate(shiftDays(now, -29)), to: toIsoDate(now) }),
  },
  {
    id: 'last90',
    label: 'Last 90 days',
    resolve: (now) => ({ from: toIsoDate(shiftDays(now, -89)), to: toIsoDate(now) }),
  },
  {
    id: 'mtd',
    label: 'Month to date',
    resolve: (now) => {
      const first = new Date(now.getFullYear(), now.getMonth(), 1);
      return { from: toIsoDate(first), to: toIsoDate(now) };
    },
  },
  { id: 'custom', label: 'Custom\u2026', resolve: () => null },
];

/** Returns the resolved range for a preset, using `now` for relative ones. */
export function resolvePreset(
  preset: DateRangePresetId,
  now: Date = new Date(),
): AnalyticsDateRange | null {
  return PRESETS.find((p) => p.id === preset)?.resolve(now) ?? null;
}

/** Convenience: the dashboard default ("Last 7 days"). */
export function getDefaultRange(): AnalyticsDateRange {
  return resolvePreset('last7') as AnalyticsDateRange;
}

// ────────────────────────── Component ────────────────────────────

export interface DateRangePickerProps {
  value: AnalyticsDateRange;
  onChange: (range: AnalyticsDateRange) => void;
  /**
   * Optional initial preset hint for the label. If omitted, the picker
   * tries to detect the matching preset from `value`; if no preset
   * matches, it shows "Custom".
   */
  initialPreset?: DateRangePresetId;
}

function detectPreset(value: AnalyticsDateRange): DateRangePresetId {
  const now = new Date();
  for (const preset of PRESETS) {
    const resolved = preset.resolve(now);
    if (resolved && resolved.from === value.from && resolved.to === value.to) {
      return preset.id;
    }
  }
  return 'custom';
}

function formatHumanRange(range: AnalyticsDateRange): string {
  if (range.from === range.to) return range.from;
  return `${range.from} \u2192 ${range.to}`;
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  value,
  onChange,
  initialPreset,
}) => {
  const [open, setOpen] = useState(false);
  const [preset, setPreset] = useState<DateRangePresetId>(
    initialPreset ?? detectPreset(value),
  );
  const [customFrom, setCustomFrom] = useState(value.from);
  const [customTo, setCustomTo] = useState(value.to);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close popover on outside click.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // Keep custom inputs in sync if `value` changes from outside.
  useEffect(() => {
    setCustomFrom(value.from);
    setCustomTo(value.to);
    setPreset(detectPreset(value));
  }, [value.from, value.to]);

  const buttonLabel = useMemo(() => {
    const def = PRESETS.find((p) => p.id === preset);
    if (!def || preset === 'custom') return formatHumanRange(value);
    return def.label;
  }, [preset, value]);

  const handlePresetClick = (id: DateRangePresetId) => {
    if (id === 'custom') {
      setPreset('custom');
      return;
    }
    const resolved = resolvePreset(id);
    if (resolved) {
      setPreset(id);
      setCustomFrom(resolved.from);
      setCustomTo(resolved.to);
      onChange(resolved);
      setOpen(false);
    }
  };

  const handleApplyCustom = () => {
    if (!customFrom || !customTo) return;
    if (customFrom > customTo) return;
    onChange({ from: customFrom, to: customTo });
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative inline-block text-left">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <svg className="h-4 w-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        <span className="whitespace-nowrap">{buttonLabel}</span>
        <svg className="h-4 w-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 011.08 1.04l-4.25 4.4a.75.75 0 01-1.08 0l-4.25-4.4a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {open && (
        <div
          className="absolute right-0 z-30 mt-2 w-72 origin-top-right rounded-lg border border-gray-200 bg-white shadow-lg focus:outline-none"
          role="listbox"
        >
          <ul className="py-1">
            {PRESETS.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => handlePresetClick(p.id)}
                  className={`flex w-full items-center justify-between px-4 py-2 text-left text-sm hover:bg-gray-50 ${
                    preset === p.id ? 'text-[#EAB308] font-semibold' : 'text-gray-700'
                  }`}
                >
                  <span>{p.label}</span>
                  {preset === p.id && (
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M16.7 5.3a1 1 0 00-1.4 0L8 12.6l-3.3-3.3a1 1 0 10-1.4 1.4l4 4a1 1 0 001.4 0l8-8a1 1 0 000-1.4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </button>
              </li>
            ))}
          </ul>
          {preset === 'custom' && (
            <div className="border-t border-gray-200 px-4 py-3 space-y-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-500">From</label>
                <input
                  type="date"
                  value={customFrom}
                  max={customTo || undefined}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-500">To</label>
                <input
                  type="date"
                  value={customTo}
                  min={customFrom || undefined}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30"
                />
              </div>
              <button
                type="button"
                onClick={handleApplyCustom}
                disabled={!customFrom || !customTo || customFrom > customTo}
                className="w-full rounded-md bg-[#EAB308] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#A16207] disabled:cursor-not-allowed disabled:opacity-50"
              >
                Apply
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
